(function () {
  'use strict';

  if (window.UnifiedErrors) {
    return;
  }

  const VERSION = '1.1.0';
  const MAX_HISTORY = 200;

  const history = [];

  const VALID_SEVERITIES = [
    'info',
    'warning',
    'error',
    'fatal'
  ];

  const SENSITIVE_KEYS = new Set([
    'email',
    'email_address',
    'phone',
    'phone_number',
    'telephone',
    'tel',
    'name',
    'full_name',
    'first_name',
    'last_name',
    'partner_name',
    'message',
    'body',
    'address',
    'street_address',
    'budget',
    'budget_value',
    'link_url',
    'href',
    'url',
    'raw_value',
    'field_value',
    'location_value',
    'event_date'
  ]);

  let sequence = 0;

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function normalizeSeverity(
    severity
  ) {
    const candidate =
      String(
        severity || 'error'
      )
        .trim()
        .toLowerCase();

    if (
      VALID_SEVERITIES.includes(
        candidate
      )
    ) {
      return candidate;
    }

    return 'error';
  }

  function normalizeCode(
    code
  ) {
    return String(
      code || 'unknown_error'
    )
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9_]+/g,
        '_'
      );
  }

  function normalizeMessage(
    message
  ) {
    return String(
      message ||
      'Unknown Unified Tracking error.'
    )
      .trim()
      .slice(
        0,
        500
      );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  /*
   * ------------------------------------------------------------
   * PRIVACY SANITIZATION
   * ------------------------------------------------------------
   */

  function sanitizeValue(
    value,
    depth
  ) {
    const currentDepth =
      Number.isFinite(depth)
        ? depth
        : 0;

    if (
      currentDepth > 4
    ) {
      return '[max-depth]';
    }

    if (
      value === null ||
      typeof value ===
        'undefined' ||
      typeof value ===
        'boolean' ||
      typeof value ===
        'number'
    ) {
      return value;
    }

    if (
      typeof value ===
      'string'
    ) {
      return value.slice(
        0,
        300
      );
    }

    if (
      value instanceof Error
    ) {
      return {
        name:
          value.name || 'Error',

        message:
          String(
            value.message || ''
          ).slice(
            0,
            300
          )
      };
    }

    if (
      Array.isArray(value)
    ) {
      return value
        .slice(
          0,
          25
        )
        .map(
          function (item) {
            return sanitizeValue(
              item,
              currentDepth + 1
            );
          }
        );
    }

    if (
      isPlainObject(value)
    ) {
      const result = {};

      Object.keys(value)
        .slice(
          0,
          50
        )
        .forEach(
          function (key) {
            const normalizedKey =
              String(key)
                .trim()
                .toLowerCase();

            if (
              SENSITIVE_KEYS.has(
                normalizedKey
              )
            ) {
              result[key] =
                '[redacted]';

              return;
            }

            result[key] =
              sanitizeValue(
                value[key],
                currentDepth + 1
              );
          }
        );

      return result;
    }

    return String(value)
      .slice(
        0,
        300
      );
  }

  function sanitizeDetails(
    details
  ) {
    if (
      !isPlainObject(
        details
      )
    ) {
      return {};
    }

    return sanitizeValue(
      details,
      0
    );
  }

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  /*
   * ------------------------------------------------------------
   * BUS EMISSION
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Avoid recursive error reporting.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                new Date()
                  .toISOString(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * STORAGE
   * ------------------------------------------------------------
   */

  function save(
    entry
  ) {
    history.push(
      entry
    );

    if (
      history.length >
      MAX_HISTORY
    ) {
      history.splice(
        0,
        history.length -
          MAX_HISTORY
      );
    }
  }

  /*
   * ------------------------------------------------------------
   * MAIN REPORTER
   * ------------------------------------------------------------
   */

  function report(
    severity,
    code,
    message,
    details
  ) {
    sequence += 1;

    const normalizedSeverity =
      normalizeSeverity(
        severity
      );

    const entry = {
      id:
        sequence,

      timestamp:
        new Date()
          .toISOString(),

      severity:
        normalizedSeverity,

      code:
        normalizeCode(
          code
        ),

      message:
        normalizeMessage(
          message
        ),

      details:
        sanitizeDetails(
          details
        )
    };

    save(
      entry
    );

    /*
     * Bus receives only sanitized data.
     */

    emit(
      'error_reported',
      {
        id:
          entry.id,

        severity:
          entry.severity,

        code:
          entry.code,

        message:
          entry.message,

        details:
          entry.details
      }
    );

    /*
     * Console output only when debug
     * or severity is fatal.
     */

    if (
      isDebugEnabled() ||
      normalizedSeverity ===
        'fatal'
    ) {
      let method =
        'info';

      if (
        normalizedSeverity ===
        'warning'
      ) {
        method =
          'warn';
      }

      if (
        normalizedSeverity ===
          'error' ||
        normalizedSeverity ===
          'fatal'
      ) {
        method =
          'error';
      }

      console[method](
        '[Unified Errors] ' +
          entry.code +
          ' · ' +
          entry.message,
        clone(
          entry.details
        )
      );
    }

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * QUERY HELPERS
   * ------------------------------------------------------------
   */

  function getAll() {
    return clone(
      history
    );
  }

  function getRecent(
    limit
  ) {
    const requested =
      Number(limit);

    const count =
      Number.isFinite(
        requested
      ) &&
      requested > 0
        ? Math.floor(
            requested
          )
        : 20;

    return clone(
      history.slice(
        -count
      )
    );
  }

  function findByCode(
    code
  ) {
    const normalized =
      normalizeCode(
        code
      );

    return clone(
      history.filter(
        function (entry) {
          return (
            entry.code ===
            normalized
          );
        }
      )
    );
  }

  function findBySeverity(
    severity
  ) {
    const normalized =
      normalizeSeverity(
        severity
      );

    return clone(
      history.filter(
        function (entry) {
          return (
            entry.severity ===
            normalized
          );
        }
      )
    );
  }

  function getStats() {
    const bySeverity = {
      info: 0,
      warning: 0,
      error: 0,
      fatal: 0
    };

    const byCode = {};

    history.forEach(
      function (entry) {
        bySeverity[
          entry.severity
        ] =
          (
            bySeverity[
              entry.severity
            ] || 0
          ) + 1;

        byCode[
          entry.code
        ] =
          (
            byCode[
              entry.code
            ] || 0
          ) + 1;
      }
    );

    return {
      version:
        VERSION,

      total:
        history.length,

      history_limit:
        MAX_HISTORY,

      by_severity:
        bySeverity,

      by_code:
        byCode
    };
  }

  function printHistory() {
    console.table(
      history.map(
        function (entry) {
          return {
            id:
              entry.id,

            timestamp:
              entry.timestamp,

            severity:
              entry.severity,

            code:
              entry.code,

            message:
              entry.message
          };
        }
      )
    );

    return getAll();
  }

  function clear() {
    history.length = 0;

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Errors] History cleared.'
      );
    }

    return true;
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedErrors = {
    version:
      VERSION,

    report:
      report,

    info:
      function (
        code,
        message,
        details
      ) {
        return report(
          'info',
          code,
          message,
          details
        );
      },

    warning:
      function (
        code,
        message,
        details
      ) {
        return report(
          'warning',
          code,
          message,
          details
        );
      },

    error:
      function (
        code,
        message,
        details
      ) {
        return report(
          'error',
          code,
          message,
          details
        );
      },

    fatal:
      function (
        code,
        message,
        details
      ) {
        return report(
          'fatal',
          code,
          message,
          details
        );
      },

    getAll:
      getAll,

    getRecent:
      getRecent,

    findByCode:
      findByCode,

    findBySeverity:
      findBySeverity,

    getStats:
      getStats,

    printHistory:
      printHistory,

    clear:
      clear,

    sanitizeDetails:
      sanitizeDetails
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Errors] Error Registry initialized',
      {
        version:
          VERSION,

        historyLimit:
          MAX_HISTORY
      }
    );
  }
})();